import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import {
  convertLocalToUTC,
  CONSOLE_RUN_TIMEZONE_UNSET_NOTE_DETAIL,
  formatDateOnlyInTimezone,
  getConsoleRunDisplayTimezone,
  getDefaultScheduledDatetimeLocal,
  getRunDayTimeSlotInTimezone,
  getUserTimezone,
  isConsoleRunTimezoneUnset,
  scheduledDateToDatetimeLocalInput,
  scheduledDateToDatetimeLocalInTimezone,
  convertDatetimeLocalToUTCInTimezone,
} from "../../utils/dateTimeUtils";
import { PACE_RANGES } from "../../constants/clubConstants";
import { useBlobUrlMapForFileSlots } from "../../hooks/useBlobUrlMapForFileSlots";
import {
  isGreaterLosAngelesCity,
  isSouthFloridaCity,
  getConsoleRegionOptions,
  getClubRegionForFilter,
  getApiErrorMessage,
  getCityFromSearchParams,
  getConsoleCityUrl,
  getConsoleClubUrl,
  getConsoleMeetingsRangeFromSearchParams,
  MAX_CAROUSEL_IMAGES,
  buildImagesArrayFromUrls,
  dedupeImageUrlsPreserveOrder,
  normalizeExistingImages,
  normalizeImageUrlForDedupe,
  collectUrlsFromCarouselSlots,
} from "../../utils/consoleUtils";
import { ArrowLeft } from "lucide-react";
import ConsoleViewShell from "./ConsoleViewShell";
import "../../styles/console.css";
import ConsoleRunFormModal from "./ConsoleRunFormModal";
import ConsoleRunsListPanel from "./ConsoleRunsListPanel";
import {
  collapseRecurringMeetingsForRunsTable,
  formatRunScheduleOccurrenceLabel,
  getRunCompletenessWithFields,
  RUN_COMPLETENESS_GATE_LABELS,
  isConsoleRunRecurringSeries,
  meetingToRun,
} from "../../utils/consoleRunsMeetingModel";
import {
  DAY_ORDER,
  EFFORT_LABEL,
  EFFORT_OPTIONS,
  PACE_PILLS_MAX,
  REL_LABEL,
  REL_OPTIONS_CREATE,
  RUN_TYPES,
  SURFACE_OPTIONS,
  syncWhatToExpectTextareaHeight,
  TYPE_LABEL,
  WORKFLOW_LABEL,
} from "../../utils/consoleRunsConstants";
import { recomputeScheduledDateTimeLocalForTimezoneChange } from "../../utils/consoleRunFormHelpers";
import { serializeRunCarouselSlotsForDirty } from "../../utils/consoleUnsavedFormGuards";


/** Build edit-run form from a run (meeting) object. Reuses same shape as add-run form. */
function runToEditForm(run) {
  if (!run) return null;
  const difficulty = run.difficulty || "easy";
  const effortOption = EFFORT_OPTIONS.find((o) => o.backend === difficulty);
  const effort = effortOption ? effortOption.value : EFFORT_OPTIONS[0].value;
  const paceValues = (run.paces || [])
    .map((label) => PACE_RANGES.find((pr) => pr.label === label)?.value)
    .filter(Boolean)
    .slice(0, PACE_PILLS_MAX);
  const recurrence =
    run.recurrence_pattern && run.recurrence_pattern !== "one-time"
      ? run.recurrence_pattern
      : "one-time";
  const eventType =
    RUN_TYPES.some((t) => t.value === run.event_type) ? run.event_type : RUN_TYPES[0].value;
  const surface =
    SURFACE_OPTIONS.some((s) => s.value === run.surface) ? run.surface : SURFACE_OPTIONS[0].value;
  return {
    club_id: run.club_id || "",
    name: run.name || run.runName || "",
    scheduledDateTimeLocal: scheduledDateToDatetimeLocalInTimezone(
      run.scheduled_date,
      getConsoleRunDisplayTimezone(run)
    ),
    timezone: run.timezone || "",
    address: run.address || run.startLocation || "",
    city: run.city || "",
    state: run.state || "",
    what_to_expect: run.what_to_expect || "",
    recurrence: recurrence === "one-time" ? "one-time" : recurrence,
    event_type: eventType,
    effort,
    surface,
    paces: paceValues,
    is_public: run.is_public !== false,
    reliability: run.reliability || "regular",
  };
}

/** Club "Good to know" bullets (same as Clubs console; max 3). */
const EDIT_RUN_CLUB_NOTES_SLOTS = 3;

function padClubGoodToKnowSlots(raw) {
  const arr = Array.isArray(raw) ? raw : [];
  return Array.from({ length: EDIT_RUN_CLUB_NOTES_SLOTS }, (_, i) => {
    const v = arr[i];
    if (v == null) return "";
    const s = typeof v === "string" ? v : String(v);
    return s;
  });
}

function normalizeClubGoodToKnowForPayload(slots) {
  return (Array.isArray(slots) ? slots : [])
    .map((n) => (n ?? "").trim())
    .filter(Boolean)
    .slice(0, EDIT_RUN_CLUB_NOTES_SLOTS);
}

function clubGoodToKnowSlotsChanged(initialSlots, currentSlots) {
  return (
    JSON.stringify(normalizeClubGoodToKnowForPayload(initialSlots)) !==
    JSON.stringify(normalizeClubGoodToKnowForPayload(currentSlots))
  );
}

export default function RunsView() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cityId = getCityFromSearchParams(searchParams);
  const meetingsRange = getConsoleMeetingsRangeFromSearchParams(searchParams);
  const pathMatch = location.pathname.match(/^\/console\/runs\/([^/]+)$/);
  const runIdFromPath = pathMatch ? pathMatch[1] : null;
  const isDetailMode = Boolean(runIdFromPath);
  const { user: currentUser } = useAuth();

  const [search, setSearch] = useState("");
  const [filterSlot, setFilterSlot] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterEffort, setFilterEffort] = useState("");
  const [filterReliability, setFilterReliability] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [sortKey, setSortKey] = useState("day");
  const [sortDir, setSortDir] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  /** Run detail: selected carousel image URL (null = show primary). */
  const [runDetailCarouselPreviewUrl, setRunDetailCarouselPreviewUrl] = useState(null);

  const { data: meetings, loading, error, refetch: refetchMeetings } = useConsoleFetch(
    async () => {
      if (!cityId) return [];
      const PAGE_SIZE = 500;
      const MAX_TOTAL = 10000;
      const params = { city_id: cityId, skip: 0, limit: PAGE_SIZE };
      if (meetingsRange && meetingsRange !== "all") params.range_key = meetingsRange;
      let all = [];
      let skip = 0;
      while (true) {
        const pageParams = { ...params, skip, limit: PAGE_SIZE };
        const page = await consoleApi.meetings.list(pageParams);
        const list = Array.isArray(page) ? page : [];
        all = all.concat(list);
        if (list.length < PAGE_SIZE || all.length >= MAX_TOTAL) break;
        skip += PAGE_SIZE;
      }
      return all;
    },
    [cityId, meetingsRange],
    { refetchOnWindowFocus: false }
  );

  const { data: clubs, refetch: refetchClubs } = useConsoleFetch(
    async () => {
      if (!cityId) return [];
      const res = await consoleApi.clubs.list({ city_id: cityId, limit: 500 });
      return Array.isArray(res) ? res : [];
    },
    [cityId],
    { refetchOnWindowFocus: false }
  );

  const [scrapedImageDeletingUrl, setScrapedImageDeletingUrl] = useState(null);

  const { data: detailRun, loading: detailRunLoading, error: detailRunError } = useConsoleFetch(
    () => (runIdFromPath ? consoleApi.meetings.get(runIdFromPath) : Promise.resolve(null)),
    [runIdFromPath],
    { refetchOnWindowFocus: false }
  );

  const clubById = useMemo(() => {
    const map = {};
    (clubs || []).forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [clubs]);

  const runs = useMemo(() => {
    const collapsed = collapseRecurringMeetingsForRunsTable(meetings || []);
    return collapsed.map(meetingToRun).filter((r) => r != null && r.id != null && r.id !== "");
  }, [meetings]);

  const useSouthFloridaRegions = isSouthFloridaCity(cityId);
  const useGreaterLaRegions = isGreaterLosAngelesCity(cityId);
  const regions = useMemo(
    () => getConsoleRegionOptions(cityId, clubs || []),
    [cityId, clubs]
  );

  const filteredAndSorted = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    const list = runs.filter((r) => {
      if (filterSlot && r.slot !== filterSlot) return false;
      if (filterType && (r.event_type || r.runType) !== filterType) return false;
      if (filterEffort && r.effortLevel !== filterEffort) return false;
      if (filterReliability && r.reliability !== filterReliability) return false;
      if (filterRegion) {
        const club = clubById[r.club_id];
        if (
          !club ||
          getClubRegionForFilter(club, useSouthFloridaRegions, useGreaterLaRegions) !== filterRegion
        )
          return false;
      }
      if (searchLower) {
        const clubName = (clubById[r.club_id]?.name || r.club_name || "").toLowerCase();
        const hay = `${clubName} ${r.runName || ""} ${r.startLocation || ""} ${r.notes || ""}`.toLowerCase();
        if (!hay.includes(searchLower)) return false;
      }
      return true;
    });
    return [...list].sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      if (sortKey === "day") {
        va = DAY_ORDER[a.day];
        vb = DAY_ORDER[b.day];
      } else if (sortKey === "club_id") {
        const an = clubById[a.club_id]?.name ?? a.club_name;
        const bn = clubById[b.club_id]?.name ?? b.club_name;
        va = (an != null ? String(an) : "").toLowerCase();
        vb = (bn != null ? String(bn) : "").toLowerCase();
      } else if (sortKey === "runName") {
        va = (a.runName != null ? a.runName : "").toString().toLowerCase();
        vb = (b.runName != null ? b.runName : "").toString().toLowerCase();
      } else if (sortKey === "runType" || sortKey === "effortLevel" || sortKey === "reliability") {
        va = (a[sortKey] != null ? a[sortKey] : "").toString().toLowerCase();
        vb = (b[sortKey] != null ? b[sortKey] : "").toString().toLowerCase();
      } else if (sortKey === "startLocation") {
        va = (a.startLocation != null ? a.startLocation : "").toString().toLowerCase();
        vb = (b.startLocation != null ? b.startLocation : "").toString().toLowerCase();
      }
      if (va < vb) return -sortDir;
      if (va > vb) return sortDir;
      return 0;
    });
  }, [
    runs,
    search,
    filterSlot,
    filterType,
    filterEffort,
    filterReliability,
    filterRegion,
    sortKey,
    sortDir,
    clubById,
    useSouthFloridaRegions,
    useGreaterLaRegions,
  ]);

  const totalFiltered = filteredAndSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const paginatedRuns = useMemo(
    () => filteredAndSorted.slice((page - 1) * pageSize, page * pageSize),
    [filteredAndSorted, page, pageSize]
  );

  // Reset to page 1 when filters/sort shrink the list past current page
  useEffect(() => {
    if (page > totalPages && totalPages >= 1) setPage(1);
  }, [page, totalPages]);

  const handleSort = useCallback(
    (key) => {
      setSortDir((prev) => (sortKey === key ? -prev : 1));
      setSortKey(key);
    },
    [sortKey]
  );

  const [deleteRunDialog, setDeleteRunDialog] = useState(null);
  const [deleteRunSubmitting, setDeleteRunSubmitting] = useState(false);
  /** Which delete action is in flight (for button labels). */
  const [deleteRunPendingKind, setDeleteRunPendingKind] = useState(null);
  const [regeocodeBusy, setRegeocodeBusy] = useState(false);

  const runDeleteMeetingRequest = useCallback(
    async (meetingId, { deleteEntireSeries = false, pendingKind = null, onDeleted = null } = {}) => {
      const id = meetingId != null ? String(meetingId).trim() : "";
      if (!id) {
        toast.error("Missing run id");
        return;
      }
      setDeleteRunSubmitting(true);
      setDeleteRunPendingKind(pendingKind);
      try {
        await consoleApi.meetings.delete(id, { deleteEntireSeries });
        toast.success(deleteEntireSeries ? "Recurring series deleted" : "Run deleted");
        setDeleteRunDialog(null);
        if (typeof onDeleted === "function") onDeleted();
        await refetchMeetings();
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to delete run"));
      } finally {
        setDeleteRunSubmitting(false);
        setDeleteRunPendingKind(null);
      }
    },
    [refetchMeetings]
  );

  const handleDeleteRunClick = useCallback(
    (run) => {
      if (!run?.id) return;
      if (isConsoleRunRecurringSeries(run)) {
        setDeleteRunDialog({ run });
        return;
      }
      const label = run.runName || run.id;
      if (!window.confirm(`Delete run "${label}"? This cannot be undone.`)) return;
      void runDeleteMeetingRequest(run.id, { deleteEntireSeries: false });
    },
    [runDeleteMeetingRequest]
  );

  const hasCity = Boolean(cityId);

  const handleRegeocodeMissingCoords = useCallback(async () => {
    if (!cityId) return;
    const ok = window.confirm(
      "Fill map coordinates for up to 100 runs in this city that have an address but are missing latitude/longitude? " +
        "This calls the geocoder and may take a minute."
    );
    if (!ok) return;
    setRegeocodeBusy(true);
    try {
      const res = await consoleApi.meetings.regeocodeMissingCoordinates({
        city_id: cityId,
        limit: 100,
        dry_run: false,
      });
      const u = typeof res.updated === "number" ? res.updated : 0;
      const f = typeof res.failed === "number" ? res.failed : 0;
      const uu = typeof res.unchanged === "number" ? res.unchanged : 0;
      toast.success(`Geocode backfill: ${u} updated, ${uu} unchanged, ${f} failed (scanned ${res.scanned ?? 0}).`);
      await refetchMeetings();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Geocode backfill failed"));
    } finally {
      setRegeocodeBusy(false);
    }
  }, [cityId, refetchMeetings]);

  // ——— Create Run modal (same fields as mobile run club create) ———
  const getEmptyAddRunForm = useCallback(() => ({
    club_id: "",
    name: "",
    scheduledDateTimeLocal: getDefaultScheduledDatetimeLocal(),
    timezone: "",
    address: "",
    city: "",
    state: "",
    what_to_expect: "",
    recurrence: "one-time",
    event_type: RUN_TYPES[0].value,
    effort: EFFORT_OPTIONS[0].value,
    surface: SURFACE_OPTIONS[0].value,
    paces: [],
    is_public: true,
    status: "upcoming",
    reliability: "regular",
  }), []);

  const [showAddRunModal, setShowAddRunModal] = useState(false);
  const [addRunForm, setAddRunForm] = useState(getEmptyAddRunForm);
  const [addRunSubmitting, setAddRunSubmitting] = useState(false);
  const [addRunOrganizerId, setAddRunOrganizerId] = useState(null);
  const [addRunOrganizerLoading, setAddRunOrganizerLoading] = useState(false);
  /** Ordered run image slots: existing URL picks or files to upload after create. */
  const [addRunImageSlots, setAddRunImageSlots] = useState([]);
  const addRunBlobById = useBlobUrlMapForFileSlots(addRunImageSlots);
  /** Good to know bullets for the selected club (same as Edit run / Clubs console). */
  const [addRunClubGoodToKnow, setAddRunClubGoodToKnow] = useState(() =>
    Array.from({ length: EDIT_RUN_CLUB_NOTES_SLOTS }, () => "")
  );
  const addRunInitialClubGoodToKnowRef = useRef(
    Array.from({ length: EDIT_RUN_CLUB_NOTES_SLOTS }, () => "")
  );
  /** Last club_id we hydrated Good to know from (avoid resetting while typing when club list refetches). */
  const addRunGoodToKnowClubIdRef = useRef(null);
  /** JSON.stringify snapshot when add-run modal opened (prefill or empty). */
  const addRunOpenBaselineFormRef = useRef(null);
  const addRunOpenBaselineImagesRef = useRef(null);
  /** Same for edit-run modal. */
  const editRunOpenBaselineFormRef = useRef(null);
  const editRunOpenBaselineImagesRef = useRef(null);
  const [unsavedRunModalKind, setUnsavedRunModalKind] = useState(null);

  const openAddRunModal = useCallback(() => {
    const emptyForm = getEmptyAddRunForm();
    setAddRunForm(emptyForm);
    setAddRunOrganizerId(null);
    setAddRunOrganizerLoading(false);
    setAddRunImageSlots([]);
    addRunOpenBaselineFormRef.current = JSON.stringify(emptyForm);
    addRunOpenBaselineImagesRef.current = serializeRunCarouselSlotsForDirty([]);
    setShowAddRunModal(true);
  }, [getEmptyAddRunForm]);

  const closeAddRunModal = useCallback(() => {
    setUnsavedRunModalKind(null);
    setShowAddRunModal(false);
    setAddRunForm(getEmptyAddRunForm());
    setAddRunOrganizerId(null);
    setAddRunOrganizerLoading(false);
    setAddRunImageSlots([]);
    const emptyGtk = Array.from({ length: EDIT_RUN_CLUB_NOTES_SLOTS }, () => "");
    setAddRunClubGoodToKnow(emptyGtk);
    addRunInitialClubGoodToKnowRef.current = emptyGtk.slice();
    addRunGoodToKnowClubIdRef.current = null;
    addRunOpenBaselineFormRef.current = null;
    addRunOpenBaselineImagesRef.current = null;
  }, [getEmptyAddRunForm]);

  // ——— Edit Run modal ———
  const [showEditRunModal, setShowEditRunModal] = useState(false);
  const [editRunMeeting, setEditRunMeeting] = useState(null);
  const [editRunForm, setEditRunForm] = useState(() => getEmptyAddRunForm());
  const [editRunSubmitting, setEditRunSubmitting] = useState(false);
  /** Recurring series: ask whether to apply edits to all future occurrences before saving. */
  const [editRunSeriesScopeOpen, setEditRunSeriesScopeOpen] = useState(false);
  const [editRunPasteUrlSaving, setEditRunPasteUrlSaving] = useState(false);
  const [editRunImageSlots, setEditRunImageSlots] = useState([]);
  const editRunBlobById = useBlobUrlMapForFileSlots(editRunImageSlots);
  /** Good to know bullets for the linked club (app run card); max 3 strings. */
  const [editRunClubGoodToKnow, setEditRunClubGoodToKnow] = useState(() =>
    Array.from({ length: EDIT_RUN_CLUB_NOTES_SLOTS }, () => "")
  );
  const editRunInitialClubGoodToKnowRef = useRef(
    Array.from({ length: EDIT_RUN_CLUB_NOTES_SLOTS }, () => "")
  );
  const addRunWhatToExpectRef = useRef(null);
  const editRunWhatToExpectRef = useRef(null);

  const openEditRunModal = useCallback(
    (run) => {
      if (!run?.id) return;
      const form = runToEditForm(run);
      if (!form) return;
      setEditRunMeeting(run);
      setEditRunForm(form);
      const club = run.club_id ? clubById[run.club_id] : null;
      const padded = padClubGoodToKnowSlots(club?.club_notes);
      setEditRunClubGoodToKnow(padded);
      editRunInitialClubGoodToKnowRef.current = padded.slice();
      const existing = normalizeExistingImages(run.images || []);
      const slotList = existing.map((img, i) => ({
        id: `rimg-${i}-${String(img.url).slice(-40).replace(/[^\w]/g, "")}`,
        kind: "url",
        url: img.url,
      }));
      setEditRunImageSlots(slotList);
      editRunOpenBaselineFormRef.current = JSON.stringify(form);
      editRunOpenBaselineImagesRef.current = serializeRunCarouselSlotsForDirty(slotList);
      setShowEditRunModal(true);
    },
    [clubById]
  );

  const closeEditRunModal = useCallback(() => {
    setUnsavedRunModalKind(null);
    setShowEditRunModal(false);
    setEditRunMeeting(null);
    setEditRunForm(getEmptyAddRunForm());
    setEditRunImageSlots([]);
    setEditRunSubmitting(false);
    setEditRunSeriesScopeOpen(false);
    const empty = Array.from({ length: EDIT_RUN_CLUB_NOTES_SLOTS }, () => "");
    setEditRunClubGoodToKnow(empty);
    editRunInitialClubGoodToKnowRef.current = empty.slice();
    editRunOpenBaselineFormRef.current = null;
    editRunOpenBaselineImagesRef.current = null;
  }, [getEmptyAddRunForm]);

  const isAddRunDirty = useCallback(() => {
    if (!showAddRunModal || addRunOpenBaselineFormRef.current == null) return false;
    const formDirty = JSON.stringify(addRunForm) !== addRunOpenBaselineFormRef.current;
    const imgDirty =
      serializeRunCarouselSlotsForDirty(addRunImageSlots) !== addRunOpenBaselineImagesRef.current;
    const gtkDirty = clubGoodToKnowSlotsChanged(
      addRunInitialClubGoodToKnowRef.current,
      addRunClubGoodToKnow
    );
    return formDirty || imgDirty || gtkDirty;
  }, [showAddRunModal, addRunForm, addRunImageSlots, addRunClubGoodToKnow]);

  const isEditRunDirty = useCallback(() => {
    if (!showEditRunModal || editRunOpenBaselineFormRef.current == null) return false;
    const formDirty = JSON.stringify(editRunForm) !== editRunOpenBaselineFormRef.current;
    const imgDirty =
      serializeRunCarouselSlotsForDirty(editRunImageSlots) !== editRunOpenBaselineImagesRef.current;
    const gtkDirty = clubGoodToKnowSlotsChanged(
      editRunInitialClubGoodToKnowRef.current,
      editRunClubGoodToKnow
    );
    return formDirty || imgDirty || gtkDirty;
  }, [showEditRunModal, editRunForm, editRunImageSlots, editRunClubGoodToKnow]);

  const tryCloseAddRunModal = useCallback(() => {
    if (!isAddRunDirty()) {
      closeAddRunModal();
      return;
    }
    setUnsavedRunModalKind("add");
  }, [isAddRunDirty, closeAddRunModal]);

  const tryCloseEditRunModal = useCallback(() => {
    if (!isEditRunDirty()) {
      closeEditRunModal();
      return;
    }
    setUnsavedRunModalKind("edit");
  }, [isEditRunDirty, closeEditRunModal]);

  const handleDeleteRunFromEditModal = useCallback(() => {
    if (!editRunMeeting?.id) return;
    const run = editRunMeeting;
    if (isConsoleRunRecurringSeries(run)) {
      closeEditRunModal();
      setDeleteRunDialog({ run });
      return;
    }
    const label = run.runName || run.id;
    if (!window.confirm(`Delete run "${label}"? This cannot be undone.`)) return;
    void runDeleteMeetingRequest(run.id, {
      deleteEntireSeries: false,
      onDeleted: closeEditRunModal,
    });
  }, [editRunMeeting, runDeleteMeetingRequest, closeEditRunModal]);

  const handleEditRunFormChange = useCallback((field, value) => {
    setEditRunForm((prev) => {
      if (field === "timezone") {
        const nextLocal = recomputeScheduledDateTimeLocalForTimezoneChange(
          prev.scheduledDateTimeLocal,
          prev.timezone,
          value
        );
        return { ...prev, timezone: value, scheduledDateTimeLocal: nextLocal };
      }
      return { ...prev, [field]: value };
    });
  }, []);

  // Apply prefill when navigating from Club detail (+ Add Run) or other console flows that set location.state.prefilledForm
  useEffect(() => {
    const state = location.state;
    if (!state?.openAddRunModalWithPrefill || !state?.prefilledForm) return;
    const merged = { ...getEmptyAddRunForm(), ...state.prefilledForm };
    setAddRunForm(merged);
    addRunOpenBaselineFormRef.current = JSON.stringify(merged);
    addRunOpenBaselineImagesRef.current = serializeRunCarouselSlotsForDirty([]);
    setShowAddRunModal(true);
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, getEmptyAddRunForm, navigate]);

  useEffect(() => {
    setRunDetailCarouselPreviewUrl(null);
  }, [runIdFromPath]);

  useEffect(() => {
    if (!showAddRunModal || !addRunForm.club_id) {
      setAddRunOrganizerId(null);
      setAddRunOrganizerLoading(false);
      return;
    }
    let cancelled = false;
    setAddRunOrganizerLoading(true);
    consoleApi.clubs
      .getOrganizerId(addRunForm.club_id)
      .then((id) => {
        if (!cancelled) {
          setAddRunOrganizerId(id);
          setAddRunOrganizerLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAddRunOrganizerId(null);
          setAddRunOrganizerLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showAddRunModal, addRunForm.club_id]);

  useEffect(() => {
    if (!showAddRunModal) {
      addRunGoodToKnowClubIdRef.current = null;
      return;
    }
    const cid = (addRunForm.club_id ?? "").trim();
    if (cid === addRunGoodToKnowClubIdRef.current) return;
    addRunGoodToKnowClubIdRef.current = cid;
    if (!cid) {
      const empty = Array.from({ length: EDIT_RUN_CLUB_NOTES_SLOTS }, () => "");
      setAddRunClubGoodToKnow(empty);
      addRunInitialClubGoodToKnowRef.current = empty.slice();
      return;
    }
    const club = clubById[cid];
    const padded = padClubGoodToKnowSlots(club?.club_notes);
    setAddRunClubGoodToKnow(padded);
    addRunInitialClubGoodToKnowRef.current = padded.slice();
  }, [showAddRunModal, addRunForm.club_id, clubById]);

  useLayoutEffect(() => {
    if (!showAddRunModal) return;
    syncWhatToExpectTextareaHeight(addRunWhatToExpectRef.current);
  }, [showAddRunModal, addRunForm.what_to_expect]);

  useLayoutEffect(() => {
    if (!showEditRunModal) return;
    syncWhatToExpectTextareaHeight(editRunWhatToExpectRef.current);
  }, [showEditRunModal, editRunForm.what_to_expect]);

  const handleAddRunFormChange = useCallback((field, value) => {
    setAddRunForm((prev) => {
      if (field === "timezone") {
        const nextLocal = recomputeScheduledDateTimeLocalForTimezoneChange(
          prev.scheduledDateTimeLocal,
          prev.timezone,
          value
        );
        return { ...prev, timezone: value, scheduledDateTimeLocal: nextLocal };
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const removeScrapedImageFromClub = useCallback(
    async (clubId, storedUrl, displayUrl) => {
      if (!clubId || storedUrl == null || storedUrl === "") return;
      if (
        !window.confirm(
          "Remove this image from the club's scraped pool? It will no longer appear in run modals or Edit club."
        )
      ) {
        return;
      }
      const club = clubById[clubId];
      const raw = Array.isArray(club?.scraped_images) ? club.scraped_images : [];
      const next = raw.filter((u) => u !== storedUrl);
      const busyKey =
        (typeof displayUrl === "string" && displayUrl.trim()) ||
        (typeof storedUrl === "string" ? storedUrl.trim() : "");
      setScrapedImageDeletingUrl(busyKey);
      try {
        await consoleApi.clubs.update(clubId, { scraped_images: next });
        await refetchClubs();
        const trimTarget = (typeof displayUrl === "string" ? displayUrl : storedUrl).trim();
        setAddRunImageSlots((prev) =>
          prev.filter((s) => !(s.kind === "url" && (typeof s.url === "string" ? s.url.trim() : "") === trimTarget))
        );
        setEditRunImageSlots((prev) =>
          prev.filter((s) => !(s.kind === "url" && (typeof s.url === "string" ? s.url.trim() : "") === trimTarget))
        );
        toast.success("Removed scraped image from club.");
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to remove scraped image"));
      } finally {
        setScrapedImageDeletingUrl(null);
      }
    },
    [clubById, refetchClubs]
  );

  const handleAddRunSubmit = useCallback(async () => {
    const { club_id, name, scheduledDateTimeLocal, address, event_type, effort, surface, paces, is_public, status: runStatus, reliability } = addRunForm;
    const nameTrim = (name ?? "").trim();
    const addressTrim = (address ?? "").trim();
    if (!club_id) {
      toast.error("Select a club.");
      return;
    }
    if (!nameTrim) {
      toast.error("Run name is required (max 30 characters for run club runs).");
      return;
    }
    if (nameTrim.length > 30) {
      toast.error("Run name must be 30 characters or less for run club runs.");
      return;
    }
    if (!addressTrim) {
      toast.error("Address is required.");
      return;
    }
    const creatorId = addRunOrganizerId || currentUser?.id;
    if (!creatorId) {
      toast.error("This club has no organizer; run cannot be created. Link an organizer to the club first.");
      return;
    }
    const formTimezone = (addRunForm.timezone ?? "").trim();
    let scheduled_date;
    if (formTimezone) {
      try {
        scheduled_date = convertDatetimeLocalToUTCInTimezone(scheduledDateTimeLocal, formTimezone);
      } catch (e) {
        toast.error("Invalid date and time.");
        return;
      }
    } else {
      const userTimezone = getUserTimezone();
      const localDate = new Date(scheduledDateTimeLocal);
      if (Number.isNaN(localDate.getTime())) {
        toast.error("Invalid date and time.");
        return;
      }
      scheduled_date = convertLocalToUTC(localDate, userTimezone);
    }
    if (new Date(scheduled_date).getTime() < Date.now() - 60000) {
      toast.error("Date and time must be in the future.");
      return;
    }
    const effortOption = EFFORT_OPTIONS.find((o) => o.value === effort);
    const difficulty = effortOption ? effortOption.backend : "easy";
    const pacesList = Array.isArray(paces) ? paces : [];
    const paceLabels = pacesList
      .slice(0, PACE_PILLS_MAX)
      .map((code) => PACE_RANGES.find((pr) => pr.value === code)?.label)
      .filter(Boolean);
    const club = clubById[club_id];
    const recurrence = addRunForm.recurrence ?? "one-time";
    const isRecurring = recurrence !== "one-time";
    const payload = {
      club_id,
      club_name: club?.name ?? undefined,
      name: nameTrim,
      scheduled_date,
      address: addressTrim,
      city: (addRunForm.city ?? "").trim() || undefined,
      state: (addRunForm.state ?? "").trim() || undefined,
      what_to_expect: (addRunForm.what_to_expect ?? "").trim() || undefined,
      meeting_type: isRecurring ? "recurring" : "one_time",
      is_recurring: isRecurring,
      recurrence_pattern: isRecurring ? recurrence : null,
      event_type: event_type || undefined,
      difficulty,
      surface: surface || undefined,
      paces: paceLabels && paceLabels.length > 0 ? paceLabels : undefined,
      is_public: is_public !== false,
      status: runStatus || "upcoming",
      reliability: reliability || undefined,
      created_by: creatorId,
    };
    if (club?.city_id) payload.city_id = club.city_id;
    if (formTimezone) payload.timezone = formTimezone;
    setAddRunSubmitting(true);
    try {
      const created = await consoleApi.meetings.create(payload);
      const meetingId = created?.id;

      let goodToKnowSaved = false;
      if (
        club_id &&
        clubGoodToKnowSlotsChanged(addRunInitialClubGoodToKnowRef.current, addRunClubGoodToKnow)
      ) {
        try {
          await consoleApi.clubs.update(club_id, {
            club_notes: normalizeClubGoodToKnowForPayload(addRunClubGoodToKnow),
          });
          addRunInitialClubGoodToKnowRef.current = addRunClubGoodToKnow.slice();
          goodToKnowSaved = true;
          await refetchClubs();
        } catch (clubErr) {
          toast.error(
            getApiErrorMessage(
              clubErr,
              "Run created, but updating club Good to know failed. Edit the club from Clubs to retry."
            )
          );
          refetchMeetings();
          refetchClubs();
          closeAddRunModal();
          return;
        }
      }

      if (meetingId && addRunImageSlots.length > 0) {
        try {
          const urls = await collectUrlsFromCarouselSlots(
            addRunImageSlots,
            (file) => consoleApi.uploadMeetingImage(meetingId, file),
            {
              onUploadError: (i, uploadErr) => {
                toast.error(getApiErrorMessage(uploadErr, `Failed to upload image ${i + 1}`));
              },
              uploadPastedUrl: (imageUrl) =>
                consoleApi.uploadMeetingImageFromUrl(meetingId, imageUrl),
            }
          );
          const images = buildImagesArrayFromUrls(urls).slice(0, MAX_CAROUSEL_IMAGES);
          if (images.length > 0) {
            await consoleApi.meetings.update(meetingId, { images });
          }
        } catch (imgErr) {
          toast.error(getApiErrorMessage(imgErr, "Run was created but saving images failed. You can edit the run to add images."));
          refetchMeetings();
          closeAddRunModal();
          return;
        }
      }
      toast.success(goodToKnowSaved ? "Run created. Good to know updated." : "Run created.");
      refetchMeetings();
      closeAddRunModal();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to create run"));
    } finally {
      setAddRunSubmitting(false);
    }
  }, [
    addRunForm,
    addRunOrganizerId,
    addRunImageSlots,
    addRunClubGoodToKnow,
    currentUser?.id,
    clubById,
    closeAddRunModal,
    refetchMeetings,
    refetchClubs,
  ]);

  const executeEditRunSave = useCallback(
    async (updateAllFutureRuns) => {
      if (!editRunMeeting?.id) return;
      const {
        name,
        scheduledDateTimeLocal,
        address,
        event_type,
        effort,
        surface,
        paces,
        is_public,
        reliability,
      } = editRunForm;
      const nameTrim = (name ?? "").trim();
      const addressTrim = (address ?? "").trim();
      if (!nameTrim) {
        toast.error("Run name is required (max 30 characters).");
        return;
      }
      if (nameTrim.length > 30) {
        toast.error("Run name must be 30 characters or less.");
        return;
      }
      if (!addressTrim) {
        toast.error("Address is required.");
        return;
      }
      const formTimezone = (editRunForm.timezone ?? "").trim();
      let scheduled_date;
      if (formTimezone) {
        try {
          scheduled_date = convertDatetimeLocalToUTCInTimezone(scheduledDateTimeLocal, formTimezone);
        } catch (e) {
          toast.error("Invalid date and time.");
          return;
        }
      } else {
        const userTimezone = getUserTimezone();
        const localDate = new Date(scheduledDateTimeLocal);
        if (Number.isNaN(localDate.getTime())) {
          toast.error("Invalid date and time.");
          return;
        }
        scheduled_date = convertLocalToUTC(localDate, userTimezone);
      }
      const effortOption = EFFORT_OPTIONS.find((o) => o.value === effort);
      const difficulty = effortOption ? effortOption.backend : "easy";
      const pacesList = Array.isArray(paces) ? paces : [];
      const paceLabels = pacesList
        .slice(0, PACE_PILLS_MAX)
        .map((code) => PACE_RANGES.find((pr) => pr.value === code)?.label)
        .filter(Boolean);
      const recurrence = editRunForm.recurrence ?? "one-time";
      const isRecurring = recurrence !== "one-time";
      const club = editRunForm.club_id ? clubById[editRunForm.club_id] : null;
      const payload = {
        name: nameTrim,
        scheduled_date,
        address: addressTrim,
        city: (editRunForm.city ?? "").trim() || undefined,
        state: (editRunForm.state ?? "").trim() || undefined,
        what_to_expect: (editRunForm.what_to_expect ?? "").trim() || undefined,
        meeting_type: isRecurring ? "recurring" : "one_time",
        is_recurring: isRecurring,
        recurrence_pattern: isRecurring ? recurrence : null,
        event_type: event_type || undefined,
        difficulty,
        surface: surface || undefined,
        paces: paceLabels && paceLabels.length > 0 ? paceLabels : undefined,
        is_public: is_public !== false,
        reliability: reliability || undefined,
      };
      if (club?.name) payload.club_name = club.name;
      if (formTimezone) payload.timezone = formTimezone;
      setEditRunSubmitting(true);
      try {
        const urls = await collectUrlsFromCarouselSlots(
          editRunImageSlots,
          (file) => consoleApi.uploadMeetingImage(editRunMeeting.id, file),
          {
            onUploadError: (i, uploadErr) => {
              toast.error(getApiErrorMessage(uploadErr, `Failed to upload image ${i + 1}`));
            },
            uploadPastedUrl: (imageUrl) =>
              consoleApi.uploadMeetingImageFromUrl(editRunMeeting.id, imageUrl),
          }
        );
        const images = buildImagesArrayFromUrls(urls).slice(0, MAX_CAROUSEL_IMAGES);
        await consoleApi.meetings.update(
          editRunMeeting.id,
          { ...payload, images },
          { updateAllFutureRuns }
        );
        const clubId = (editRunForm.club_id ?? "").trim();
        let goodToKnowSaved = false;
        if (
          clubId &&
          clubGoodToKnowSlotsChanged(editRunInitialClubGoodToKnowRef.current, editRunClubGoodToKnow)
        ) {
          try {
            await consoleApi.clubs.update(clubId, {
              club_notes: normalizeClubGoodToKnowForPayload(editRunClubGoodToKnow),
            });
            editRunInitialClubGoodToKnowRef.current = editRunClubGoodToKnow.slice();
            goodToKnowSaved = true;
            refetchClubs();
          } catch (clubErr) {
            toast.error(
              getApiErrorMessage(clubErr, "Run saved, but updating club Good to know failed. Try again.")
            );
            refetchMeetings();
            refetchClubs();
            return;
          }
        }
        toast.success(goodToKnowSaved ? "Run and Good to know updated." : "Run updated.");
        refetchMeetings();
        closeEditRunModal();
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to update run"));
      } finally {
        setEditRunSubmitting(false);
      }
    },
    [
      editRunMeeting,
      editRunForm,
      editRunImageSlots,
      editRunClubGoodToKnow,
      clubById,
      closeEditRunModal,
      refetchMeetings,
      refetchClubs,
    ]
  );

  const handleEditRunSubmit = useCallback(async () => {
    if (!editRunMeeting?.id) return;
    const nameTrim = (editRunForm.name ?? "").trim();
    if (!nameTrim) {
      toast.error("Run name is required (max 30 characters).");
      return;
    }
    if (nameTrim.length > 30) {
      toast.error("Run name must be 30 characters or less.");
      return;
    }
    if (!(editRunForm.address ?? "").trim()) {
      toast.error("Address is required.");
      return;
    }
    if (isConsoleRunRecurringSeries(editRunMeeting)) {
      setEditRunSeriesScopeOpen(true);
      return;
    }
    await executeEditRunSave(true);
  }, [editRunMeeting, editRunForm, executeEditRunSave]);

  const handleSaveEditRunPasteUrl = useCallback(
    async (pastedUrl) => {
      if (!editRunMeeting?.id) return false;
      const u = (pastedUrl ?? "").trim();
      if (!u.startsWith("http")) {
        toast.error("Enter a valid HTTPS image URL.");
        return false;
      }
      if (editRunImageSlots.length >= MAX_CAROUSEL_IMAGES) {
        toast.error(`At most ${MAX_CAROUSEL_IMAGES} run images.`);
        return false;
      }
      setEditRunPasteUrlSaving(true);
      try {
        const current = await consoleApi.meetings.get(editRunMeeting.id);
        const prevUrls = normalizeExistingImages(current.images || []).map((img) => img.url);
        const { url: stored } = await consoleApi.uploadMeetingImageFromUrl(editRunMeeting.id, u);
        if (!stored) throw new Error("No URL returned from image import");
        const mergedUrls = dedupeImageUrlsPreserveOrder([...prevUrls, stored]).slice(0, MAX_CAROUSEL_IMAGES);
        if (mergedUrls.length === prevUrls.length) {
          toast.info("That image is already on this run.");
          return true;
        }
        await consoleApi.meetings.update(editRunMeeting.id, {
          images: buildImagesArrayFromUrls(mergedUrls),
        });
        const updated = await consoleApi.meetings.get(editRunMeeting.id);
        setEditRunMeeting(updated);
        setEditRunImageSlots((prev) => {
          const hasSlot = prev.some((s) => {
            if (s.kind !== "url" && s.kind !== "pasted_url") return false;
            const su = s.url != null ? String(s.url).trim() : "";
            if (!su.startsWith("http")) return false;
            return normalizeImageUrlForDedupe(su) === normalizeImageUrlForDedupe(stored);
          });
          let next;
          if (hasSlot || prev.length >= MAX_CAROUSEL_IMAGES) {
            next = prev;
          } else {
            next = [...prev, { id: `rimg-saved-${Date.now()}`, kind: "url", url: stored }];
          }
          editRunOpenBaselineImagesRef.current = serializeRunCarouselSlotsForDirty(next);
          return next;
        });
        refetchMeetings();
        toast.success("Run image saved.");
        return true;
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to save run image"));
        return false;
      } finally {
        setEditRunPasteUrlSaving(false);
      }
    },
    [editRunMeeting, editRunImageSlots, refetchMeetings]
  );

  const renderRunDetailContent = () => {
    if (detailRunLoading) {
      return (
        <div className="console-main-inner" style={{ padding: "2rem", textAlign: "center" }}>
          <p className="console-muted-text">Loading run…</p>
        </div>
      );
    }
    if (detailRunError || !detailRun) {
      return (
        <div className="console-main-inner" style={{ padding: "2rem", textAlign: "center" }}>
          <p className="console-error-text">{typeof detailRunError === "string" ? detailRunError : detailRunError?.message || "Run not found."}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => navigate(getConsoleCityUrl("runs", cityId))}
          >
            <ArrowLeft className="h-4 w-4 mr-1.5 inline" aria-hidden />
            Back to Runs
          </Button>
        </div>
      );
    }
    const r = meetingToRun(detailRun);
    const club = clubById[detailRun.club_id];
    const { pct: rc, compFields, missingLabels } = getRunCompletenessWithFields(r);
    const fillColor = rc >= 70 ? "var(--runcards-success)" : rc >= 45 ? "var(--console-amber)" : "var(--console-red)";
    const primaryImage =
      Array.isArray(detailRun.images) && detailRun.images.length > 0
        ? detailRun.images.find((img) => img.is_primary) || detailRun.images[0]
        : null;
    const imageUrl = primaryImage?.url;
    const displayUrl = runDetailCarouselPreviewUrl || imageUrl;
    const runImages = normalizeExistingImages(detailRun.images);
    const showRunCarousel = runImages.length > 1;

    return (
      <div className="console-main-inner run-detail-page">
        <div className="run-detail-header">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="run-detail-back"
            onClick={() => navigate(getConsoleCityUrl("runs", cityId))}
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden />
            Back to Runs
          </Button>
          <div className="run-detail-title-row">
            <div className="run-detail-title-wrap">
              <div className="section-title">{r.runName || r.name || ""}</div>
              {(club?.name || r.club_name) && (
                <div className="section-sub">
                  {club?.name ?? r.club_name}
                  {[club?.city, club?.region].filter(Boolean).length > 0 && ` · ${[club.city, club.region].filter(Boolean).join(" · ")}`}
                </div>
              )}
            </div>
            <div className="run-detail-actions-inline">
              <Button variant="outline" size="sm" onClick={() => openEditRunModal(r)} className="tb-btn">
                Edit Run
              </Button>
            </div>
          </div>
          <div className="run-detail-hero-row">
            <div
              className="run-detail-hero"
              role={runDetailCarouselPreviewUrl ? "button" : undefined}
              tabIndex={runDetailCarouselPreviewUrl ? 0 : undefined}
              onClick={() => runDetailCarouselPreviewUrl && setRunDetailCarouselPreviewUrl(null)}
              onKeyDown={(e) =>
                runDetailCarouselPreviewUrl && (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setRunDetailCarouselPreviewUrl(null))
              }
              title={runDetailCarouselPreviewUrl ? "Click to show primary image" : undefined}
            >
              <div className="run-detail-hero-img-wrap">
                {displayUrl ? (
                  <img src={displayUrl} alt="" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <div className="run-detail-hero-placeholder">No run image</div>
                )}
              </div>
              {runDetailCarouselPreviewUrl && (
                <p className="text-xs" style={{ color: "var(--console-muted)", margin: 0 }}>
                  Preview — click image above to show primary image
                </p>
              )}
            </div>
            {displayUrl && (
              <div className="run-detail-hero-full" title="Full image">
                <img src={displayUrl} alt="" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            )}
            {showRunCarousel && (
              <div className="run-detail-carousel">
                {runImages.map((img, idx) => (
                  <button
                    key={img.url ? `${img.url}-${idx}` : idx}
                    type="button"
                    className={`run-detail-carousel-item ${displayUrl === img.url ? "run-detail-carousel-item-active" : ""}`}
                    onClick={() => setRunDetailCarouselPreviewUrl(img.url)}
                    title="Preview in main image"
                  >
                    <img src={img.url} alt="" onError={(e) => { e.target.style.display = "none"; }} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div
            className={`comp-dots ${rc === 100 ? "comp-dots--full" : ""}`}
            title={`Completeness: ${Object.values(RUN_COMPLETENESS_GATE_LABELS).join(" · ")}`}
          >
            {compFields.map((f) => (
              <span
                key={f.key}
                className={`cdot ${f.ok ? "yes" : "no"}`}
                title={`${RUN_COMPLETENESS_GATE_LABELS[f.key] ?? f.key}: ${f.ok ? "filled" : "missing"}`}
                aria-label={`${RUN_COMPLETENESS_GATE_LABELS[f.key] ?? f.key}: ${f.ok ? "filled" : "missing"}`}
              />
            ))}
          </div>
          <div
            className={`completeness-row ${rc === 100 ? "completeness-row--full" : ""}`}
            title="Run completeness: four fields (25% each). 70%+ = ready for app."
          >
            <div className="comp-track">
              <div className="comp-fill" style={{ width: `${rc}%`, background: fillColor }} />
            </div>
            <div className="comp-pct">{rc}%</div>
          </div>
          {rc < 100 && missingLabels.length > 0 ? (
            <div className="run-detail-completeness-missing" role="status" aria-live="polite">
              <div className="run-detail-completeness-missing-title">Still needed for 100%</div>
              <p className="run-detail-completeness-missing-hint">
                Use <strong>Edit Run</strong> and add:
              </p>
              <ul className="run-detail-completeness-missing-list">
                {missingLabels.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="run-detail-section mt-5">
          <div className="section-label">Details</div>
          <div className="run-detail-meta">
            {detailRun.club_id && (
              <p className="run-detail-meta-row">
                <strong>Club:</strong>{" "}
                <button
                  type="button"
                  className="run-detail-link"
                  onClick={() => navigate(getConsoleClubUrl(cityId, detailRun.club_id, meetingsRange))}
                >
                  {club?.name ?? r.club_name ?? detailRun.club_id}
                </button>
              </p>
            )}
            <p className="run-detail-meta-row">
              <strong>Schedule:</strong> {r.day ?? "—"} {r.time ? ` ${r.time}` : ""}
              {detailRun.recurrence_pattern && detailRun.recurrence_pattern !== "one-time" && ` · ${detailRun.recurrence_pattern}`}
            </p>
            {(detailRun.meeting_type || detailRun.is_recurring != null) && (
              <p className="run-detail-meta-row">
                <strong>Meeting type:</strong>{" "}
                {detailRun.meeting_type === "recurring" || detailRun.is_recurring ? "Recurring" : "One-time"}
              </p>
            )}
            {detailRun.recurrence_end_date && (
              <p className="run-detail-meta-row">
                <strong>Recurrence end:</strong>{" "}
                {formatDateOnlyInTimezone(detailRun.recurrence_end_date, getConsoleRunDisplayTimezone(detailRun)) || "—"}
              </p>
            )}
            <p className="run-detail-meta-row">
              <strong>Timezone:</strong>{" "}
              {!isConsoleRunTimezoneUnset(detailRun) ? (
                String(detailRun.timezone).trim()
              ) : (
                <span className="text-[var(--console-muted)]">{CONSOLE_RUN_TIMEZONE_UNSET_NOTE_DETAIL}</span>
              )}
            </p>
            {(r.runType || detailRun.event_type) && (
              <p className="run-detail-meta-row">
                <strong>Type:</strong> {TYPE_LABEL[r.runType] ?? detailRun.event_type}
              </p>
            )}
            {(r.effortLevel || detailRun.difficulty) && (
              <p className="run-detail-meta-row">
                <strong>Effort:</strong> {EFFORT_LABEL[r.effortLevel] ?? detailRun.difficulty}
              </p>
            )}
            {detailRun.surface && (
              <p className="run-detail-meta-row">
                <strong>Surface:</strong> {SURFACE_OPTIONS.find((s) => s.value === detailRun.surface)?.label ?? detailRun.surface}
              </p>
            )}
            {(r.reliability || detailRun.reliability) && (
              <p className="run-detail-meta-row">
                <strong>Reliability:</strong> {REL_LABEL[r.reliability] ?? detailRun.reliability}
              </p>
            )}
            <p className="run-detail-meta-row">
              <strong>Status:</strong> {WORKFLOW_LABEL[r.workflowStatus] ?? r.status ?? "—"}
            </p>
            {detailRun.address && (
              <p className="run-detail-meta-row">
                <strong>Address:</strong> {detailRun.address}
              </p>
            )}
            {(detailRun.city || detailRun.state) && (
              <p className="run-detail-meta-row">
                <strong>City / region:</strong> {[detailRun.city, detailRun.state].filter(Boolean).join(", ")}
              </p>
            )}
            {(detailRun.user_name || detailRun.created_by) && (
              <p className="run-detail-meta-row run-detail-meta-muted">
                <strong>Organizer (created_by):</strong>{" "}
                {detailRun.user_name ?? detailRun.created_by}
              </p>
            )}
            {detailRun.console_creator_email && (
              <p className="run-detail-meta-row run-detail-meta-muted">
                <strong>Console / admin:</strong> {detailRun.console_creator_email}
              </p>
            )}
            {detailRun.weather && typeof detailRun.weather === "object" && Object.keys(detailRun.weather).length > 0 && (
              <p className="run-detail-meta-row">
                <strong>Weather:</strong>{" "}
                {[detailRun.weather.temperature, detailRun.weather.conditions]
                  .filter((v) => v != null && v !== "")
                  .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
                  .join(" · ") || "Available"}
              </p>
            )}
            {detailRun.what_to_expect && (
              <p className="run-detail-desc">{detailRun.what_to_expect}</p>
            )}
            {Array.isArray(detailRun.paces) && detailRun.paces.length > 0 && (
              <p className="run-detail-meta-row">
                <strong>Paces:</strong> {detailRun.paces.join(", ")}
              </p>
            )}
            <p className="run-detail-meta-row">
              <strong>Public:</strong> {detailRun.is_public !== false ? "Yes" : "No"}
              {typeof detailRun.current_participants === "number" && (
                <> · <strong>Participants:</strong> {detailRun.current_participants}</>
              )}
            </p>
            {detailRun.created_at && (
              <p className="run-detail-meta-row run-detail-meta-muted">
                <strong>Created:</strong> {new Date(detailRun.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                {detailRun.updated_at && ` · Updated: ${new Date(detailRun.updated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}`}
              </p>
            )}
            {Array.isArray(detailRun.group_results) && detailRun.group_results.length > 0 && (
              <p className="run-detail-meta-row">
                <strong>Strava-linked results:</strong> {detailRun.group_results.length} result{detailRun.group_results.length !== 1 ? "s" : ""}
              </p>
            )}
            {detailRun.internal_notes && (
              <p className="run-detail-desc run-detail-internal-notes">
                <strong>Internal notes:</strong> {detailRun.internal_notes}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ConsoleViewShell loading={hasCity && loading && !isDetailMode} error={error} loadingLabel="Loading runs…">
      {!hasCity ? (
        <div className="console-main-inner" style={{ padding: "2rem", textAlign: "center" }}>
          <p style={{ color: "var(--console-muted)", fontSize: 14 }}>
            Select a city from Network overview to view runs.
          </p>
        </div>
      ) : isDetailMode ? (
        renderRunDetailContent()
      ) : (
        <ConsoleRunsListPanel
          navigate={navigate}
          cityId={cityId}
          meetingsRange={meetingsRange}
          regions={regions}
          search={search}
          setSearch={setSearch}
          filterSlot={filterSlot}
          setFilterSlot={setFilterSlot}
          filterType={filterType}
          setFilterType={setFilterType}
          filterEffort={filterEffort}
          setFilterEffort={setFilterEffort}
          filterReliability={filterReliability}
          setFilterReliability={setFilterReliability}
          filterRegion={filterRegion}
          setFilterRegion={setFilterRegion}
          runs={runs}
          totalFiltered={totalFiltered}
          paginatedRuns={paginatedRuns}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalPages={totalPages}
          clubById={clubById}
          handleSort={handleSort}
          openAddRunModal={openAddRunModal}
          openEditRunModal={openEditRunModal}
          handleDeleteRunClick={handleDeleteRunClick}
          deleteRunSubmitting={deleteRunSubmitting}
          onRegeocodeMissingCoords={handleRegeocodeMissingCoords}
          regeocodeBusy={regeocodeBusy}
        />
      )}

      <Dialog
        open={Boolean(deleteRunDialog)}
        onOpenChange={(open) => {
          if (!open && !deleteRunSubmitting) setDeleteRunDialog(null);
        }}
      >
        <DialogContent className="console-page max-w-md bg-[var(--console-surface)] border-[var(--console-border)] text-[var(--console-ink)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--console-ink)]">Delete recurring run</DialogTitle>
            <DialogDescription className="text-[var(--console-muted)]">
              Choose whether to remove only the next listed occurrence or the entire series (template and all instances).
            </DialogDescription>
          </DialogHeader>
          {deleteRunDialog?.run && (
            <div className="space-y-3 text-sm text-[var(--console-ink)]">
              <p>
                <span className="font-medium">{deleteRunDialog.run.runName || "Run"}</span>
                {deleteRunDialog.run.club_name && (
                  <span className="text-[var(--console-muted)]"> · {deleteRunDialog.run.club_name}</span>
                )}
              </p>
              <p className="text-[var(--console-muted)]">
                Occurrence in this table:{" "}
                <span className="text-[var(--console-ink)]">{formatRunScheduleOccurrenceLabel(deleteRunDialog.run)}</span>
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-col items-stretch">
            <Button
              type="button"
              variant="outline"
              className="w-full justify-center border-[var(--console-border)]"
              disabled={deleteRunSubmitting}
              onClick={() => {
                const run = deleteRunDialog?.run;
                if (!run) return;
                const oid = run._seriesNextOccurrenceId || run.id;
                void runDeleteMeetingRequest(oid, {
                  deleteEntireSeries: false,
                  pendingKind: "occurrence",
                });
              }}
            >
              {deleteRunSubmitting && deleteRunPendingKind === "occurrence"
                ? "Deleting…"
                : "Delete only this occurrence"}
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="w-full justify-center"
              disabled={deleteRunSubmitting}
              onClick={() => {
                const run = deleteRunDialog?.run;
                if (!run) return;
                void runDeleteMeetingRequest(run.id, {
                  deleteEntireSeries: true,
                  pendingKind: "series",
                });
              }}
            >
              {deleteRunSubmitting && deleteRunPendingKind === "series"
                ? "Deleting…"
                : "Delete entire series"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              disabled={deleteRunSubmitting}
              onClick={() => setDeleteRunDialog(null)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConsoleRunFormModal
        variant="add"
        open={showAddRunModal}
        onClose={tryCloseAddRunModal}
        title="Create Run (Run Club Run)"
        description="Same fields as mobile create run. Select a club; run is created as that club’s organizer."
        form={addRunForm}
        onFieldChange={handleAddRunFormChange}
        onAddressPlaceSelected={(place) => {
          setAddRunForm((prev) => ({
            ...prev,
            address: place.formattedAddress,
            city: place.city || prev.city,
            state: place.state || prev.state,
          }));
        }}
        whatToExpectRef={addRunWhatToExpectRef}
        clubs={clubs}
        organizerLoading={addRunOrganizerLoading}
        organizerId={addRunOrganizerId}
        currentUserId={currentUser?.id}
        clubGoodToKnow={addRunClubGoodToKnow}
        onClubGoodToKnowChange={(i, v) => {
          setAddRunClubGoodToKnow((prev) => {
            const next = [...prev];
            next[i] = v;
            return next;
          });
        }}
        imageSlots={addRunImageSlots}
        setImageSlots={setAddRunImageSlots}
        imageBlobById={addRunBlobById}
        fileInputId="add-run-images-file"
        appendSlotKeyPrefix="arf"
        clubForCarousel={addRunForm.club_id ? clubById[addRunForm.club_id] : null}
        onRemoveScrapedFromClub={(storedUrl, displayUrl) =>
          removeScrapedImageFromClub(addRunForm.club_id, storedUrl, displayUrl)
        }
        scrapedRemoveBusyDisplayUrl={scrapedImageDeletingUrl}
        onSubmit={handleAddRunSubmit}
        submitting={addRunSubmitting}
        submitLabel={addRunSubmitting ? "Creating…" : "Create Run"}
        submitDisabled={
          !addRunForm.club_id || addRunOrganizerLoading || (!addRunOrganizerId && !currentUser?.id)
        }
        recurringToggleLabel="Make this recurring"
      />

      <ConsoleRunFormModal
        variant="edit"
        open={showEditRunModal}
        onClose={tryCloseEditRunModal}
        title="Edit Run"
        description="Change details or run images. Use Choose Files, Paste image URL (imported on save or via Save next to the URL field), or club hero/carousel/scraped picks when shown below."
        form={editRunForm}
        onFieldChange={handleEditRunFormChange}
        onAddressPlaceSelected={(place) => {
          setEditRunForm((prev) => ({
            ...prev,
            address: place.formattedAddress,
            city: place.city || prev.city,
            state: place.state || prev.state,
          }));
        }}
        whatToExpectRef={editRunWhatToExpectRef}
        clubs={clubs}
        organizerLoading={false}
        organizerId={null}
        currentUserId={currentUser?.id}
        editSubtitle={
          editRunMeeting ? (
            <>
              Club:{" "}
              <strong>
                {clubById[editRunForm.club_id]?.name ?? editRunMeeting.club_name ?? editRunForm.club_id}
              </strong>
            </>
          ) : null
        }
        clubGoodToKnow={editRunClubGoodToKnow}
        onClubGoodToKnowChange={(i, v) => {
          setEditRunClubGoodToKnow((prev) => {
            const next = [...prev];
            next[i] = v;
            return next;
          });
        }}
        imageSlots={editRunImageSlots}
        setImageSlots={setEditRunImageSlots}
        imageBlobById={editRunBlobById}
        fileInputId="edit-run-images-file"
        appendSlotKeyPrefix="erf"
        clubForCarousel={editRunForm.club_id ? clubById[editRunForm.club_id] : null}
        onRemoveScrapedFromClub={(storedUrl, displayUrl) =>
          removeScrapedImageFromClub(editRunForm.club_id, storedUrl, displayUrl)
        }
        scrapedRemoveBusyDisplayUrl={scrapedImageDeletingUrl}
        onSubmit={handleEditRunSubmit}
        submitting={editRunSubmitting}
        submitLabel={editRunSubmitting ? "Saving…" : "Save changes"}
        submitDisabled={!editRunForm.name?.trim() || !editRunForm.address?.trim()}
        recurringToggleLabel="Recurring run"
        onSavePastedRunImageUrl={handleSaveEditRunPasteUrl}
        savePastedRunImageUrlBusy={editRunPasteUrlSaving}
        onDeleteRun={handleDeleteRunFromEditModal}
        deleteRunBusy={deleteRunSubmitting && showEditRunModal}
      />

      <AlertDialog open={editRunSeriesScopeOpen} onOpenChange={setEditRunSeriesScopeOpen}>
        <AlertDialogContent className="console-page bg-[var(--console-surface)] border-[var(--console-border)] text-[var(--console-ink)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--console-ink)]">Apply to future runs?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--console-muted)]">
              This run is part of a recurring series. Choose whether changes apply only to this occurrence,
              or to every upcoming run in the series (details, time alignment, and template images when
              applicable).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="tb-btn w-full sm:w-auto"
              disabled={editRunSubmitting}
              onClick={() => {
                setEditRunSeriesScopeOpen(false);
                void executeEditRunSave(false);
              }}
            >
              Only this occurrence
            </Button>
            <Button
              type="button"
              className="tb-btn w-full sm:w-auto"
              disabled={editRunSubmitting}
              onClick={() => {
                setEditRunSeriesScopeOpen(false);
                void executeEditRunSave(true);
              }}
            >
              All future runs in this series
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={unsavedRunModalKind != null}
        onOpenChange={(next) => {
          if (!next) setUnsavedRunModalKind(null);
        }}
      >
        <AlertDialogContent className="console-page bg-[var(--console-surface)] border-[var(--console-border)] text-[var(--console-ink)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--console-ink)]">Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--console-muted)]">
              You have unsaved changes. If you leave now, they will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="tb-btn">Keep editing</AlertDialogCancel>
            <AlertDialogAction
              className="tb-btn bg-[var(--console-red)] hover:opacity-90"
              onClick={() => {
                if (unsavedRunModalKind === "add") closeAddRunModal();
                else if (unsavedRunModalKind === "edit") closeEditRunModal();
                setUnsavedRunModalKind(null);
              }}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConsoleViewShell>
  );
}
